import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { getOrganizationById, updateUser } from "../api/axios";
import { useToast } from "../hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { 
  Edit2, 
  Save, 
  X, 
  Lock, 
  User, 
  Mail, 
  Building2, 
  Calendar,
  CheckCircle2,
  AlertCircle,
  Loader2
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

// Form schema for profile update
const profileSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters").max(30, "Username cannot exceed 30 characters"),
  displayname: z.string().min(3, "Display name must be at least 3 characters").max(30, "Display name cannot exceed 30 characters"),
  email: z.string().email("Please provide a valid email address"),
});

// Password change schema
const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ProfileFormValues = z.infer<typeof profileSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

export default function ProfilePage() {
  const { user, loading, refreshSession } = useAuth();
  const { toast } = useToast();
  const [org, setOrg] = useState<any>(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      username: user?.username || "",
      displayname: user?.displayname || user?.displayName || "",
      email: user?.email || "",
    },
  });

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      profileForm.reset({
        username: user.username || "",
        displayname: user.displayname || user.displayName || "",
        email: user.email || "",
      });
    }
  }, [user]);

  useEffect(() => {
    if (user && user.organization && /^[a-fA-F0-9]{24}$/.test(user.organization)) {
      setOrgLoading(true);
      getOrganizationById(user.organization)
        .then(setOrg)
        .catch(() => setOrgError("Failed to load organization info."))
        .finally(() => setOrgLoading(false));
    }
  }, [user]);

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    profileForm.reset({
      username: user?.username || "",
      displayname: user?.displayname || user?.displayName || "",
      email: user?.email || "",
    });
  };

  const onProfileSubmit = async (data: ProfileFormValues) => {
    if (!user || !isAdmin) {
      toast({
        title: "Permission Denied",
        description: "Only administrators can edit profiles.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateUser(user._id || user.id, {
        username: data.username,
        displayname: data.displayname,
        email: data.email,
      });
      
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });
      
      setIsEditing(false);
      await refreshSession();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || error.message || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordFormValues) => {
    if (!user || !isAdmin) {
      toast({
        title: "Permission Denied",
        description: "Only administrators can change passwords.",
        variant: "destructive",
      });
      return;
    }

    setIsChangingPassword(true);
    try {
      // Note: Backend update endpoint requires admin role and accepts password
      // However, we'd typically need a separate endpoint for password change with current password verification
      // For now, we'll use the update endpoint (this might need backend adjustment)
      await updateUser(user._id || user.id, {
        password: data.newPassword,
      });
      
      toast({
        title: "Password Changed",
        description: "Your password has been changed successfully.",
      });
      
      setIsPasswordDialogOpen(false);
      passwordForm.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || error.message || "Failed to change password",
        variant: "destructive",
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">User not found</h3>
            <p className="text-sm text-muted-foreground">Please log in to view your profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">My Profile</h1>
            <p className="text-muted-foreground mt-1">Manage your account information and settings</p>
          </div>
          {isAdmin && !isEditing && (
            <Button onClick={handleEdit} variant="outline" className="flex items-center gap-2">
              <Edit2 className="h-4 w-4" />
              Edit Profile
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Profile Card */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    <CardTitle>User Information</CardTitle>
                  </div>
                  {isAdmin && isEditing && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancel}
                        disabled={isSaving}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={profileForm.handleSubmit(onProfileSubmit)}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-1" />
                        )}
                        Save
                      </Button>
                    </div>
                  )}
                </div>
                <CardDescription>
                  {isEditing ? "Update your profile information" : "Your personal account details"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        {...profileForm.register("username")}
                        disabled={isSaving}
                      />
                      {profileForm.formState.errors.username && (
                        <p className="text-sm text-red-600">
                          {profileForm.formState.errors.username.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="displayname">Display Name</Label>
                      <Input
                        id="displayname"
                        {...profileForm.register("displayname")}
                        disabled={isSaving}
                      />
                      {profileForm.formState.errors.displayname && (
                        <p className="text-sm text-red-600">
                          {profileForm.formState.errors.displayname.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        {...profileForm.register("email")}
                        disabled={isSaving}
                      />
                      {profileForm.formState.errors.email && (
                        <p className="text-sm text-red-600">
                          {profileForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-muted-foreground">Username</Label>
                        <p className="text-base font-medium">{user.username}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-muted-foreground">Display Name</Label>
                        <p className="text-base font-medium">{user.displayname || user.displayName || "-"}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-muted-foreground">Email</Label>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <p className="text-base">{user.email}</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-muted-foreground">Role</Label>
                        <div>
                          <Badge variant={user.role === "superadmin" ? "default" : "secondary"}>
                            {user.role?.charAt(0).toUpperCase() + user.role?.slice(1) || "Unknown"}
                          </Badge>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-muted-foreground">Status</Label>
                        <div>
                          {user.active ? (
                            <Badge variant="default" className="bg-green-600">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-muted-foreground">Account Created</Label>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <p className="text-base">
                            {user.createdAt ? new Date(user.createdAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }) : "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Password Change Card */}
            {isAdmin && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Lock className="h-5 w-5 text-primary" />
                    <CardTitle>Password</CardTitle>
                  </div>
                  <CardDescription>Change your account password</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    onClick={() => setIsPasswordDialogOpen(true)}
                    className="w-full"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Change Password
                  </Button>
                </CardContent>
              </Card>
            )}

            {!isAdmin && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <AlertCircle className="h-5 w-5" />
                    <p className="text-sm">
                      Profile editing is only available for administrators. Contact your administrator to update your profile.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Organization Info */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  <CardTitle>Organization</CardTitle>
                </div>
                <CardDescription>Your organization details</CardDescription>
              </CardHeader>
              <CardContent>
                {orgLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : orgError ? (
                  <div className="text-sm text-red-600">{orgError}</div>
                ) : org ? (
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <Label className="text-muted-foreground">Name</Label>
                      <p className="text-base font-medium">{org.name}</p>
                    </div>
                    {isAdmin && (
                      <div className="space-y-1">
                        <Label className="text-muted-foreground">Join Code</Label>
                        <Badge variant="secondary" className="font-mono">
                          {org.joinCode}
                        </Badge>
                      </div>
                    )}
                    {org.createdAt && (
                      <div className="space-y-1">
                        <Label className="text-muted-foreground">Created</Label>
                        <p className="text-sm text-muted-foreground">
                          {new Date(org.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">No organization information available.</div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Password Change Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                {...passwordForm.register("currentPassword")}
                disabled={isChangingPassword}
              />
              {passwordForm.formState.errors.currentPassword && (
                <p className="text-sm text-red-600">
                  {passwordForm.formState.errors.currentPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                {...passwordForm.register("newPassword")}
                disabled={isChangingPassword}
              />
              {passwordForm.formState.errors.newPassword && (
                <p className="text-sm text-red-600">
                  {passwordForm.formState.errors.newPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                {...passwordForm.register("confirmPassword")}
                disabled={isChangingPassword}
              />
              {passwordForm.formState.errors.confirmPassword && (
                <p className="text-sm text-red-600">
                  {passwordForm.formState.errors.confirmPassword.message}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsPasswordDialogOpen(false);
                  passwordForm.reset();
                }}
                disabled={isChangingPassword}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isChangingPassword}>
                {isChangingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  "Change Password"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
